import styles from './SearchBar.module.css';
import { useSharedState } from '../hooks/useSharedState';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import magnifier from '../assets/magnifier_white.png';
import { EventModel } from '../models/EventModel';
import { useCategories } from '../hooks/useCategories';
import { useVenues } from '../hooks/useVenues';
import { findCategoryExactMatch, getCategorySuggestions, getEventSuggestions, getVenueSuggestions, SearchSuggestion } from '../utils/searchUtils';

interface SearchBarProps {
    suggestionEvents?: EventModel[];
}

const SearchBar : React.FC<SearchBarProps> = ({ suggestionEvents = [] }: SearchBarProps) => {

    const [query, setQuery] = useSharedState('searchQuery', '');
    const [, setCategories] = useSharedState('categories', '');
    const [, setVenue] = useSharedState('venue', '');
    const [_query, _setQuery] = useState(query);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const { categories } = useCategories();
    const { venues } = useVenues();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const comboboxId = useId();
    const listboxId = `${comboboxId}-suggestions`;

    useEffect(() => {
        _setQuery(query);
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setActiveIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const suggestions = useMemo(() => {
        const trimmedQuery = _query.trim();
        if (trimmedQuery.length < 2) {
            return [];
        }

        return [
            ...getEventSuggestions(suggestionEvents, trimmedQuery),
            ...getVenueSuggestions(venues, trimmedQuery),
            ...getCategorySuggestions(categories, trimmedQuery),
        ];
    }, [_query, categories, suggestionEvents, venues]);

    const groupedSuggestions = useMemo(() => {
        return suggestions.reduce<Record<string, SearchSuggestion[]>>((groups, suggestion) => {
            if (!groups[suggestion.groupLabel]) {
                groups[suggestion.groupLabel] = [];
            }
            groups[suggestion.groupLabel].push(suggestion);
            return groups;
        }, {});
    }, [suggestions]);

    const handleInputChange = (e: any) => {
        _setQuery(e.target.value);
        setIsOpen(true);
        setActiveIndex(-1);
    };
    
    const handleSubmit = (e: any) => {
        e.preventDefault();
        const trimmedQuery = _query.trim();
        const matchingCategory = findCategoryExactMatch(categories, trimmedQuery);

        if (matchingCategory) {
            setCategories(matchingCategory.slug);
            setVenue('');
            setQuery('');
            _setQuery('');
            setIsOpen(false);
            setActiveIndex(-1);
            return;
        }

        setVenue('');
        setQuery(trimmedQuery);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const applySuggestion = (suggestion: SearchSuggestion) => {
        if (suggestion.type === 'category') {
            setCategories(suggestion.value);
            setVenue('');
            setQuery('');
            _setQuery('');
        } else if (suggestion.type === 'venue') {
            setVenue(suggestion.value);
            setQuery(suggestion.label);
            _setQuery(suggestion.label);
        } else {
            setVenue('');
            setQuery(suggestion.label);
            _setQuery(suggestion.label);
        }

        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown' && suggestions.length > 0) {
            e.preventDefault();
            setIsOpen(true);
            setActiveIndex((current) => (current + 1) % suggestions.length);
            return;
        }

        if (e.key === 'ArrowUp' && suggestions.length > 0) {
            e.preventDefault();
            setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
            return;
        }

        if (e.key === 'Escape') {
            setIsOpen(false);
            setActiveIndex(-1);
            return;
        }

        if(e.key == 'Enter') {
            if (isOpen && activeIndex >= 0 && suggestions[activeIndex]) {
                e.preventDefault();
                applySuggestion(suggestions[activeIndex]);
                return;
            }
            handleSubmit(e);
        }
    }

    const activeSuggestionId =
        isOpen && activeIndex >= 0 && suggestions[activeIndex]
            ? `${comboboxId}-option-${activeIndex}`
            : undefined;

    return <div className={styles.searchWrapper} ref={wrapperRef}>
        <form className={styles.searchBar} onSubmit={handleSubmit}>
            <input
                id={comboboxId}
                type="text"
                className={styles.searchInput}
                placeholder="Rechercher un événement"
                value={_query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (suggestions.length > 0) {
                        setIsOpen(true);
                    }
                }}
                role="combobox"
                aria-label="Rechercher un evenement"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeSuggestionId}
            />

            {_query && (
                <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => { _setQuery(''); setQuery(''); setVenue(''); setIsOpen(false); }}
                    aria-label="Effacer la recherche"
                    title="Effacer la recherche"
                >×</button>
            )}

            <button type="submit" className={styles.searchButton} aria-label="Lancer la recherche" title="Lancer la recherche">
                <img src={magnifier} alt="Lancer la recherche"/>
            </button>
        </form>
        {isOpen && suggestions.length > 0 && (
            <div className={styles.suggestions} id={listboxId} role="listbox" aria-label="Suggestions de recherche">
                {Object.entries(groupedSuggestions).map(([groupLabel, groupSuggestions], groupIndex) => {
                    const groupId = `${comboboxId}-group-${groupIndex}`;

                    return (
                    <div
                        key={groupLabel}
                        className={styles.suggestionGroup}
                        role="group"
                        aria-labelledby={groupId}
                    >
                        <div className={styles.groupLabel} id={groupId}>{groupLabel}</div>
                        {groupSuggestions.map((suggestion) => {
                            const suggestionIndex = suggestions.findIndex((item) => item.type === suggestion.type && item.label === suggestion.label && item.value === suggestion.value);
                            return (
                                <button
                                    id={`${comboboxId}-option-${suggestionIndex}`}
                                    key={`${suggestion.type}-${suggestion.value}-${suggestion.label}`}
                                    type="button"
                                    className={[styles.suggestionItem, suggestionIndex === activeIndex ? styles.activeSuggestion : ''].join(' ')}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySuggestion(suggestion)}
                                    role="option"
                                    aria-selected={suggestionIndex === activeIndex}
                                >
                                    <span>{suggestion.label}</span>
                                    {suggestion.meta && <span className={styles.suggestionMeta}>{suggestion.meta}</span>}
                                </button>
                            );
                        })}
                    </div>
                    );
                })}
            </div>
        )}
    </div>;
};


export default SearchBar;
